import { memo, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../../../components';
import type { SchedulePrintCourtRange } from './schedulePrintModel';
import { courtRangesOverlap } from './schedulePrintModel';

type SchedulePrintCourtRangePickerProps = {
  courtNames: string[];
  ranges: SchedulePrintCourtRange[] | null;
  onChange: (ranges: SchedulePrintCourtRange[] | null) => void;
};

export const SchedulePrintCourtRangePicker = memo(function SchedulePrintCourtRangePicker({
  courtNames,
  ranges,
  onChange,
}: SchedulePrintCourtRangePickerProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  const activeRanges = ranges ?? [];
  const hasOverlap = activeRanges.length > 1 && courtRangesOverlap(activeRanges);

  const addRange = useCallback(() => {
    if (courtNames.length === 0) return;
    const next: SchedulePrintCourtRange = {
      startCourtIndex: 0,
      endCourtIndex: Math.min(courtNames.length - 1, 3),
    };
    onChange([...activeRanges, next]);
  }, [activeRanges, courtNames.length, onChange]);

  const updateRange = useCallback(
    (index: number, field: 'startCourtIndex' | 'endCourtIndex', value: number) => {
      const next = activeRanges.map((range, i) => {
        if (i !== index) return range;
        const updated = { ...range, [field]: value };
        if (updated.startCourtIndex > updated.endCourtIndex) {
          if (field === 'startCourtIndex') {
            updated.endCourtIndex = value;
          } else {
            updated.startCourtIndex = value;
          }
        }
        return updated;
      });
      onChange(next);
    },
    [activeRanges, onChange]
  );

  const removeRange = useCallback(
    (index: number) => {
      const next = activeRanges.filter((_, i) => i !== index);
      onChange(next.length > 0 ? next : null);
    },
    [activeRanges, onChange]
  );

  if (courtNames.length === 0) return null;

  return (
    <div className="schedule-print-court-ranges mb-3">
      <div className="schedule-print-court-ranges__title fw-semibold mb-2">
        {t(prefix + 'schedulePrintCourtRangesTitle')}
      </div>
      {activeRanges.length === 0 ? (
        <p className="text-muted small mb-2">{t(prefix + 'schedulePrintCourtRangesAll')}</p>
      ) : (
        <ul className="schedule-print-court-ranges__list list-unstyled mb-2">
          {activeRanges.map((range, index) => (
            <li key={index} className="schedule-print-court-ranges__item d-flex flex-wrap align-items-center gap-2 mb-2">
              <span className="schedule-print-court-ranges__index">{index + 1}.</span>
              <Form.Select
                size="sm"
                className="schedule-print-court-ranges__select"
                value={range.startCourtIndex}
                onChange={(e) =>
                  updateRange(index, 'startCourtIndex', Number(e.target.value))
                }
                aria-label={t(prefix + 'schedulePrintCourtRangeFrom')}
              >
                {courtNames.map((name, courtIndex) => (
                  <option key={`from-${courtIndex}`} value={courtIndex}>
                    {name}
                  </option>
                ))}
              </Form.Select>
              <span>–</span>
              <Form.Select
                size="sm"
                className="schedule-print-court-ranges__select"
                value={range.endCourtIndex}
                onChange={(e) =>
                  updateRange(index, 'endCourtIndex', Number(e.target.value))
                }
                aria-label={t(prefix + 'schedulePrintCourtRangeTo')}
              >
                {courtNames.map((name, courtIndex) => (
                  <option key={`to-${courtIndex}`} value={courtIndex}>
                    {name}
                  </option>
                ))}
              </Form.Select>
              <CustomButton
                btnText={t(prefix + 'schedulePrintCourtRangeRemove')}
                variant="outline-danger"
                size="sm"
                cb={() => removeRange(index)}
              />
            </li>
          ))}
        </ul>
      )}
      {hasOverlap ? (
        <p className="text-danger small mb-2" role="alert">
          {t(prefix + 'schedulePrintCourtRangesOverlap')}
        </p>
      ) : null}
      <CustomButton
        btnText={t(prefix + 'schedulePrintCourtRangeAdd')}
        variant="outline-secondary"
        size="sm"
        cb={addRange}
      />
    </div>
  );
});
